<script setup lang="ts">
import {onMounted, ref} from 'vue';
import {Model} from "@/app/Model";
import Chart from 'chart.js/auto';
import Choices from "choices.js";

import {CommsResponsabilita, type IEsito} from "@/app/CommsResponsabilita";

import * as bootstrap from 'bootstrap';
import MinMaxSlider from '@/components/atomics/MinMaxSliderDashboard.vue';
import PaginazioneDashboard from "@/components/atomics/PaginazioneDashboardResponsabilita.vue";
import PaginazioneDashboardJump from "@/components/atomics/PaginazioneDashboardResponsabilitaJump.vue";
import type {KeyValoreStr, KeyValoreStrNum, KeyValoreStrNumArr, KeyValoreStrNumBool} from "@/app/data/DataTypes";
import {PdfGenerator} from "@/app/PdfGenerator";

declare let gtagO: Function;

let model: Model = Model.getReactiveInstance();

let filtersHtml = ref("");

let chartTipologiaDanno: Chart | null = null;
let chartTipologiaDannoPatrimoniale: Chart | null = null;
let chartTipologiaDannoNonPatrimoniale: Chart | null = null;
let chartDomandaAccolta: Chart | null = null;
let chartRisarcimento: Chart | null = null;
let chartDurataProcessoSingolaCitta: Chart | null = null;
let chartTipologiaDannoCitta: Chart | null = null;

let choiceCitta: Choices | null = null;
let choiceGiudice: Choices | null = null;

const CHART_COLORS = {
  gfl1: 'rgb(67,172,239)',
  gfl2: 'rgb(22, 91, 170)',
  gfl3: 'rgb(150,216,246)',
  gfl4: 'rgb(19, 51, 113)',
  gfl5: 'rgb(227, 242, 253)',
  gfl6: 'rgb(41, 98, 255)',
  gfl7: 'rgb(161, 85, 185)',
  gfl8: 'rgb(22, 191, 214)'
};

// START ----------------------
onMounted(() => {
  checkStart();
});
function checkStart(): void {
  if (!Model.res_setupped) {
    setTimeout(() => checkStart(), 100)
  } else if (!CommsResponsabilita.listAllDone) {
    CommsResponsabilita.getInstance().listAll(() => {
      checkStart();
    });
  } else {
    initComponents();
  }
}

// MAIN ----------------------
function changeDashboardAt(field: string = ""): void {
  let valueField = "";
  let stringField = field;

  switch (field) {
    case "risarcimento":
      valueField = model.res_flow_dashboard.risarcimento;
      stringField = "risarcimento";

      break;
    case "riferimento_normativo":
      valueField = model.res_flow_dashboard.riferimento_normativo;
      stringField = "riferimento normativo";
      break;
    case "domanda_accolta":
      valueField = model.res_flow_dashboard.domanda_accolta + "";
      stringField = "domanda accolta";
      break;
    case "tipologia_danno":
      valueField = model.res_flow_dashboard.tipologia_danno;
      stringField = "tipologia danno";
      break;
    case "tipologia_danno_patrimoniale":
      valueField = model.res_flow_dashboard.tipologia_danno_patrimoniale;
      stringField = "tipologia danno patrimoniale";
      break;
    case "tipologia_danno_non_patrimoniale":
      valueField = model.res_flow_dashboard.tipologia_danno_non_patrimoniale;
      stringField = "tipologia danno non patrimoniale";
      break;
    case "risarcimento_richiesto":
      valueField = model.res_flow_dashboard.risarcimento_richiesto_da + " - " + model.res_flow_dashboard.risarcimento_richiesto_a;
      stringField = "anno";
      break;
    case "risarcimento_disposto_totale":
      valueField = model.res_flow_dashboard.risarcimento_disposto_totale_da + " - " + model.res_flow_dashboard.risarcimento_disposto_totale_a;
      stringField = "anno";
      break;
    case "id_localita":
      valueField = model.res_flow_dashboard.id_localita;
      stringField = "città";

      if (valueField) {
        let inputLocalita: HTMLElement | null = document.getElementById("input_id_localita");
        let optionLocalita = null;

        if (inputLocalita) {
          optionLocalita = inputLocalita.querySelectorAll("option");

          if (optionLocalita) {
            for (let i = 0; i < optionLocalita.length; i++) {
              if (optionLocalita[i].selected == true) {
                valueField = optionLocalita[i].innerHTML;
              }
            }
          }
        }
      }

      break;
    case "durata_procedimento":
      valueField = model.res_flow_dashboard.durata_procedimento + "";
      stringField = "durata del procedimento";
      break;
    case "data":
      valueField = model.res_flow_dashboard.data_da + " - " + model.res_flow_dashboard.data_a;
      stringField = "anno";

      break;
    case "giudice":
      valueField = model.res_flow_dashboard.giudice;
      break;
  }

  if (valueField) {
    gtagO({
      event: 'filtro',
      filtro: stringField,
      valore: valueField,
      percorso: 'Responsabilita'
    })
  }

  callQuery();
}

function clearAllFilters(): void {
  model.res_flow_dashboard.reset();
  
  changeDashboardAt();
}

function clearSingleFilter(field: string): void {
  let canUpdate: boolean = true;

  switch (field) {
    case "":
      canUpdate = false;
      break;
    case "durata_procedimento_notNull":
      model.res_flow_dashboard.durata_procedimento_notNull = false;
      break;
    case "durata_procedimento_active":
      model.res_flow_dashboard.durata_procedimento_active = false;
      model.res_flow_dashboard.durata_procedimento = 0;
      break;
    case "risarcimento_richiesto_active":
      model.res_flow_dashboard.risarcimento_richiesto_active = false;

      model.res_flow_dashboard.risarcimento_richiesto_da = model.res_flow_dashboard.risarcimento_richiesto_min;
      model.res_flow_dashboard.risarcimento_richiesto_a =  model.res_flow_dashboard.risarcimento_richiesto_max;
      break;
    case "risarcimento_disposto_totale_active":
      model.res_flow_dashboard.risarcimento_disposto_totale_active = false;

      model.res_flow_dashboard.risarcimento_disposto_totale_da = model.res_flow_dashboard.risarcimento_disposto_totale_min;
      model.res_flow_dashboard.risarcimento_disposto_totale_a =  model.res_flow_dashboard.risarcimento_disposto_totale_max;
      break;
    case "data_active":
      model.res_flow_dashboard.data_active = false;
      model.res_flow_dashboard.data_da = model.res_flow_dashboard.data_min;
      model.res_flow_dashboard.data_a = model.res_flow_dashboard.data_max;
      break;
    default:
      //@ts-ignore
      model.res_flow_dashboard[field] = "";
      break;
  }

  if (canUpdate) changeDashboardAt();
}

//@ts-ignore
window.clearSingleFilter = clearSingleFilter;

function callQueryPaginazione(): void {
  callQuery(true);
}
function callQuery(soloPaginazione: boolean = false): void {
  //chiama i dati attuali
  CommsResponsabilita.getInstance().queryDashboard((e: IEsito) => {
    updateComponents();
  }, soloPaginazione);
}

// COMPONENTS INIT ----------------------
function initComponents(): void {
  //SIDEBAR
  model.sidebarCollapsed = false;

  //CHOICES
  initChoices();

  //TOOLTIPS
  const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  //@ts-ignore
  const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl))

  //LOAD DATA
  callQuery();
}

function initChoices(): void {
  createChoiceCitta();
  createChoiceGiudice();
}

function createChoiceCitta(): void {
  if (choiceCitta) choiceCitta.destroy();

  let choiceItems: any[] = [{
    value: "",
    label: "Tutti...",
    selected: model.res_flow_dashboard.id_localita != "" ? false : true,
    disabled: false
  }];

  let dataSourceCitta = model.res_lista_citta;
  if (model.res_get_dashboard.totale_citta && model.res_get_dashboard.totale_citta.length > 0) dataSourceCitta = model.res_get_dashboard.totale_citta;
  dataSourceCitta.forEach(x => choiceItems.push({
    value: x.id_localita,
    label: x.citta,
    selected: false,
    disabled: false
  }));

  let defaultItem: any = {
    value: "",
    label: "Tutti...",
    selected: true,
    disabled: false
  }

  if (model.res_flow_dashboard.id_localita != "") {
    defaultItem.value = model.res_flow_dashboard.id_localita;

    let useLabel: string = model.res_flow_dashboard.id_localita;
    let findCitta: KeyValoreStr | undefined = dataSourceCitta.find( (x: KeyValoreStr): boolean => { return x.id_localita == model.res_flow_dashboard.id_localita });

    if (findCitta) useLabel = findCitta.citta;
    defaultItem.label = useLabel;
  }

  let itemItems: any[] = [defaultItem];

  //@ts-ignore
  choiceCitta = new Choices(document.getElementById('input_id_localita'), {
    items: itemItems,
    choices: choiceItems,
    allowHTML: true,
    placeholder: false,
    loadingText: 'Caricamento...',
    noResultsText: 'Nessun risultato',
    noChoicesText: 'Nessun elemento da selezionare',
    itemSelectText: 'Premi per selezionare',
  });
}

function createChoiceGiudice(): void {
  if (choiceGiudice) choiceGiudice.destroy();

  let choiceItems: any[] = [{
    value: "",
    label: "Tutti...",
    selected: model.res_flow_dashboard.giudice != "" ? false : true,
    disabled: false
  }];

  let dataSourceGiudici = model.res_lista_giudici;
  if (model.res_get_dashboard.totale_giudici && model.res_get_dashboard.totale_giudici.length > 0) dataSourceGiudici = model.res_get_dashboard.totale_giudici;
  dataSourceGiudici.forEach(x => choiceItems.push({
    value: x.giudice,
    label: x.giudice,
    selected: false,
    disabled: false
  }));

  let defaultItem: any = {
    value: "",
    label: "Tutti...",
    selected: true,
    disabled: false
  }

  if (model.res_flow_dashboard.giudice != "") {
    defaultItem.value = model.res_flow_dashboard.giudice;
    defaultItem.label = model.res_flow_dashboard.giudice;
  }

  let itemItems: any[] = [defaultItem];

  //@ts-ignore
  choiceGiudice = new Choices(document.getElementById('input_giudice'), {
    items: itemItems,
    choices: choiceItems,
    allowHTML: true,
    placeholder: false,
    loadingText: 'Caricamento...',
    noResultsText: 'Nessun risultato',
    noChoicesText: 'Nessun elemento da selezionare',
    itemSelectText: 'Premi per selezionare',
  });
}

// COMPONENTS UPDATE ----------------------
function updateComponents(): void {
  //CHARTS
  updateCharts();
  //FILTRI
  updateFilters();
  //CHOICES
  updateChoices();
  //MINMAX
  updateMinMaxSliders(true);
  //MAP
  updateMap();
  //PAGINAZIONE
  updatePaginazione();
}

let chartTipologiaDannoExists: boolean = false;
let chartTipologiaDannoPatrimonialeExists: boolean = false;
let chartTipologiaDannoNonPatrimonialeExists: boolean = false;
let chartDomandaAccoltaExists: boolean = false;
let chartRisarcimentoExists: boolean = false;

let chartDurataProcessoSingolaCittaExists: boolean = false;
let chartTipologiaDannoCittaExists: boolean = false;

function updateCharts(): void {
  updateChartsTipologiaDanno();
  updateChartsTipologiaDannoPatrimoniale();
  updateChartsTipologiaDannoNonPatrimoniale();
  updateChartsDomandaAccolta();
  updateCartsRisarcimento();
  updateChartsTipologiaDannoCitta();
  updateChartsDurataProcessoSingolaCitta();
}

const getOrCreateLegendList = (chart: Chart, id:string): HTMLElement | null => {
  const legendContainer: HTMLElement | null = document.getElementById(id);
  let listContainer: HTMLElement | null = null;
  if (legendContainer) {
    listContainer = legendContainer.querySelector('ul');

    if (!listContainer) {
      listContainer = document.createElement('ul');
      listContainer.style.display = 'flex';
      listContainer.style.flexDirection = 'row';
      listContainer.style.margin = "0";
      listContainer.style.padding = "0";

      legendContainer.appendChild(listContainer);
    }
  }

  return listContainer;
};

const htmlLegendPlugin = {
  id: 'htmlLegend',
  afterUpdate(chart: any, args: any, options: any) {
    const ul = getOrCreateLegendList(chart, options.containerID);
    if (ul) {
      // Remove old legend items
      while (ul.firstChild) {
        ul.firstChild.remove();
      }
    }

    // Reuse the built-in legendItems generator
    const items = chart.options.plugins.legend.labels.generateLabels(chart);

    items.forEach( (item: any) => {
      const li = document.createElement('li');
      li.style.alignItems = 'center';
      li.style.cursor = 'auto';
      li.style.display = 'flex';
      li.style.flexDirection = 'row';
      li.style.marginLeft = '10px';

      // Color box
      const boxSpan = document.createElement('span');
      boxSpan.style.background = item.fillStyle;
      boxSpan.style.borderColor = item.strokeStyle;
      boxSpan.style.borderWidth = item.lineWidth + 'px';
      boxSpan.style.display = 'inline-block';
      boxSpan.style.flexShrink = "0";
      boxSpan.style.height = '20px';
      boxSpan.style.marginRight = '10px';
      boxSpan.style.width = '20px';

      // Text
      const textContainer = document.createElement('p');
      textContainer.style.color = item.fontColor;
      textContainer.style.margin = "0";
      textContainer.style.padding = "0";
      textContainer.style.textDecoration = item.hidden ? 'line-through' : '';

      const text = document.createTextNode(item.text);
      textContainer.appendChild(text);

      li.appendChild(boxSpan);
      li.appendChild(textContainer);
      if (ul) ul.appendChild(li);
    });
  }
};

function updateChartsTipologiaDanno(): void {
  if (Chart.getChart("canvas_tipologia_danno")) {
    Chart.getChart("canvas_tipologia_danno")?.destroy();
  }

  if (model.res_get_dashboard.tipologia_danno.length > 0) {
    const configTipologiaDanno = {
      type: 'doughnut',
      data: {
        labels: [],
        datasets: [{
          label: 'Tipologia danno',
          data: [],
          backgroundColor: Object.values(CHART_COLORS),
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container_tipologia_danno',
          },
          legend: {
            display: false
          }
        },
        radius: "85%"
      },
      plugins: [htmlLegendPlugin]
    };

    model.res_get_dashboard.tipologia_danno.forEach((x: KeyValoreStrNum) => {
      //@ts-ignore
      configTipologiaDanno.data.labels.push(x.tipologia_danno + " (" + x.percentuale + "%)");

      //@ts-ignore
      configTipologiaDanno.data.datasets[0].data.push(x.percentuale);
    });

    let canvasTipologiaDanno: HTMLElement | null = document.getElementById('canvas_tipologia_danno');

    //@ts-ignore
    chartTipologiaDanno = new Chart(canvasTipologiaDanno, configTipologiaDanno);

    if (!chartTipologiaDannoExists) {
      chartTipologiaDannoExists = true;

      //@ts-ignore
      canvasTipologiaDanno.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartTipologiaDanno.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("tipologia_danno", chartTipologiaDanno.data.labels[res[0].index]);
      };
    }
  } else {
    const divLegend: HTMLElement | null = document.getElementById("legend-container_tipologia_danno");
    if (divLegend) {
      divLegend.innerHTML = "";
    }
  }
}

function updateChartsTipologiaDannoPatrimoniale(): void {
  if (Chart.getChart("canvas_tipologia_danno_patrimoniale")) {
    Chart.getChart("canvas_tipologia_danno_patrimoniale")?.destroy();
  }

  if (model.res_get_dashboard.tipologia_danno_patrimoniale && model.res_get_dashboard.tipologia_danno_patrimoniale.length > 0) {
    const configTipologiaDannoPatrimoniale = {
      type: "doughnut",
      data: {
        labels: [],
        datasets: [{
          label: 'Danno patrimoniale',
          data: [],
          backgroundColor: Object.values(CHART_COLORS),
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container_tipologia_danno_patrimoniale',
          },
          legend: {
            display: false
          }
        },
        radius: "85%"
      },
      plugins: [htmlLegendPlugin]
    }

    model.res_get_dashboard.tipologia_danno_patrimoniale.forEach((x: KeyValoreStrNumArr) => {
      //@ts-ignore
      configTipologiaDannoPatrimoniale.data.labels.push(x.tipologia_danno_patrimoniale + " (" + x.percentuale + "%)");

      //@ts-ignore
      configTipologiaDannoPatrimoniale.data.datasets[0].data.push(x.percentuale);
    });

    let canvasTipologiaDannoPatrimoniale: HTMLElement | null = document.getElementById('canvas_tipologia_danno_patrimoniale');

    //@ts-ignore
    chartTipologiaDannoPatrimoniale = new Chart(canvasTipologiaDannoPatrimoniale, configTipologiaDannoPatrimoniale);

    if (!chartTipologiaDannoPatrimonialeExists) {
      chartTipologiaDannoPatrimonialeExists = true;

      //@ts-ignore
      canvasTipologiaDannoPatrimoniale.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartTipologiaDannoPatrimoniale.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("tipologia_danno_patrimoniale", chartTipologiaDannoPatrimoniale.data.labels[res[0].index])
      };
    }
  } else {
    const divLegend: HTMLElement | null = document.getElementById("legend-container_tipologia_danno_patrimoniale");
    if (divLegend) {
      divLegend.innerHTML = "";
    }
  }
}

function updateChartsTipologiaDannoNonPatrimoniale(): void {
  if (Chart.getChart("canvas_tipologia_danno_non_patrimoniale")) {
    Chart.getChart("canvas_tipologia_danno_non_patrimoniale")?.destroy();
  }

  if (model.res_get_dashboard.tipologia_danno_non_patrimoniale && model.res_get_dashboard.tipologia_danno_non_patrimoniale.length > 0) {
    const configTipologiaDannoNonPatrimoniale = {
      type: "doughnut",
      data: {
        labels: [],
        datasets: [{
          label: 'Danno non patrimoniale',
          data: [],
          backgroundColor: Object.values(CHART_COLORS),
          hoverOffset: 4
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container_tipologia_danno_non_patrimoniale',
          },
          legend: {
            display: false
          }
        },
        radius: "85%"
      },
      plugins: [htmlLegendPlugin]
    }

    model.res_get_dashboard.tipologia_danno_non_patrimoniale.forEach((x: KeyValoreStrNum) => {
      //@ts-ignore
      configTipologiaDannoNonPatrimoniale.data.labels.push(x.tipologia_danno_non_patrimoniale + " (" + x.percentuale + "%)");

      //@ts-ignore
      configTipologiaDannoNonPatrimoniale.data.datasets[0].data.push(x.percentuale);
    });

    let canvasDannoNonPatrimoniale: HTMLElement | null = document.getElementById('canvas_tipologia_danno_non_patrimoniale');

    //@ts-ignore
    chartTipologiaDannoNonPatrimoniale = new Chart(canvasDannoNonPatrimoniale, configTipologiaDannoNonPatrimoniale);

    if (!chartTipologiaDannoNonPatrimonialeExists) {
      chartTipologiaDannoNonPatrimonialeExists = true;

      //@ts-ignore
      canvasDannoNonPatrimoniale.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartTipologiaDannoNonPatrimoniale.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("tipologia_danno_non_patrimoniale", chartTipologiaDannoNonPatrimoniale.data.labels[res[0].index]);
      };
    }
  } else {
    const divLegend: HTMLElement | null = document.getElementById("legend-container_tipologia_danno_non_patrimoniale");
    if (divLegend) {
      divLegend.innerHTML = "";
    }
  }
}

function updateChartsDomandaAccolta(): void {
  if (Chart.getChart("canvas_domanda_accolta")) {
    Chart.getChart("canvas_domanda_accolta")?.destroy();
  }

  if (model.res_get_dashboard.domanda_accolta.length > 0) {
    const configDomandaAccolta = {
      type: 'doughnut',
      data: {
        labels: [],
        datasets: [{
          label: 'Domanda Accolta ',
          data: [],
          backgroundColor: Object.values(CHART_COLORS),
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container_domanda_accolta',
          },
          legend: {
            display: false
          }
        },
        radius: "85%"
      },
      plugins: [htmlLegendPlugin]
    };

    model.res_get_dashboard.domanda_accolta.forEach((x: KeyValoreStrNumBool) => {
      //@ts-ignore
      configDomandaAccolta.data.labels.push((x.domanda_accolta  ? "Si" : "No") + " (" + x.percentuale + "%)");

      //@ts-ignore
      configDomandaAccolta.data.datasets[0].data.push(x.percentuale);
    });

    let canvasDomandaAccolta: HTMLElement | null = document.getElementById('canvas_domanda_accolta');

    //@ts-ignore
    chartDomandaAccolta = new Chart(canvasDomandaAccolta, configDomandaAccolta);

    if (!chartDomandaAccoltaExists) {
      chartDomandaAccoltaExists = true;

      //@ts-ignore
      canvasDomandaAccolta.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartDomandaAccolta.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("domanda_accolta", chartDomandaAccolta.data.labels[res[0].index]);
      };
    }
  } else {
    const divLegend: HTMLElement | null = document.getElementById("legend-container_domanda_accolta");
    if (divLegend) {
      divLegend.innerHTML = "";
    }
  }
}

function updateCartsRisarcimento(): void {
  if (Chart.getChart("canvas_risarcimento")) {
    Chart.getChart("canvas_risarcimento")?.destroy();
  }

  if (model.res_get_dashboard.risarcimento.length > 0) {
    const configRisarcimento = {
      type: 'doughnut',
      data: {
        labels: [],
        datasets: [{
          label: 'Risarcimento',
          data: [],
          backgroundColor: Object.values(CHART_COLORS),
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container_risarcimento',
          },
          legend: {
            display: false
          }
        },
        radius: "85%"
      },
      plugins: [htmlLegendPlugin]
    };

    model.res_get_dashboard.risarcimento.forEach((x: KeyValoreStrNum) => {
      //@ts-ignore
      configRisarcimento.data.labels.push(x.risarcimento + " (" + x.percentuale + "%)");

      //@ts-ignore
      configRisarcimento.data.datasets[0].data.push(x.percentuale);
    });

    let canvasRisarcimento: HTMLElement | null = document.getElementById('canvas_risarcimento');

    //@ts-ignore
    chartRisarcimento = new Chart(canvasRisarcimento, configRisarcimento);

    if (!chartRisarcimentoExists) {
      chartRisarcimentoExists = true;

      //@ts-ignore
      canvasRisarcimento.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartRisarcimento.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("risarcimento", chartRisarcimento.data.labels[res[0].index]);
      };
    }
  } else {
    const divLegend: HTMLElement | null = document.getElementById("legend-container_risarcimento");
    if (divLegend) {
      divLegend.innerHTML = "";
    }
  }
}

function updateChartsDurataProcessoSingolaCitta(): void {
  if (Chart.getChart("canvas_durata_del_processo_per_singola_citta")) {
    Chart.getChart("canvas_durata_del_processo_per_singola_citta")?.destroy();
  }

  if (model.res_get_dashboard.durata_del_processo_per_singola_citta && model.res_get_dashboard.durata_del_processo_per_singola_citta.length > 0) {
    const configDurataProcessoSingolaCitta = {
      type: "bar",
      data: {
        labels: [],
        datasets: [
          {
            label: "Durata",
            backgroundColor: Object.values(CHART_COLORS),
            data: [],
          }
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
            onClick: null
          }
        },
        barRoundness: 1,
        scales: {
          x: {
            stacked: true,
          },
          y: {
            stacked: true
          }
        }
      }
    };

    model.res_get_dashboard.durata_del_processo_per_singola_citta.forEach((x: KeyValoreStrNum) => {
      //@ts-ignore
      configDurataProcessoSingolaCitta.data.labels.push(x.descr_provincia);
      //@ts-ignore
      configDurataProcessoSingolaCitta.data.datasets[0].data.push(x.durata_del_processo);
    });

    let canvasDurataProcessoSingolaCitta: HTMLElement | null = document.getElementById('canvas_durata_del_processo_per_singola_citta');

    //@ts-ignore
    chartDurataProcessoSingolaCitta = new Chart(canvasDurataProcessoSingolaCitta, configDurataProcessoSingolaCitta);

    if (!chartDurataProcessoSingolaCittaExists) {
      chartDurataProcessoSingolaCittaExists = true;

      //@ts-ignore
      canvasDurataProcessoSingolaCitta.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartDurataProcessoSingolaCitta.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("id_localita", chartDurataProcessoSingolaCitta.data.labels[res[0].index]);
      };
    }
  }
}

function updateChartsTipologiaDannoCitta(): void {
  if (Chart.getChart("canvas_tipologia_danno_per_citta")) {
    Chart.getChart("canvas_tipologia_danno_per_citta")?.destroy();
  }

  if (model.res_get_dashboard.tipologia_danno_per_citta && model.res_get_dashboard.tipologia_danno_per_citta.length > 0) {
    const configTipologiaDannoCitta = {
      type: "bar",
      data: {
        labels: [],
        datasets: [
          {
            label: Model.lista_tipologia_danno_per_citta[0],
            backgroundColor: CHART_COLORS.gfl1,
            data: [],
          }, {
            label: Model.lista_tipologia_danno_per_citta[1],
            backgroundColor: CHART_COLORS.gfl2,
            data: [],
          }, {
            label: Model.lista_tipologia_danno_per_citta[2],
            backgroundColor: CHART_COLORS.gfl3,
            data: [],
          }
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            onClick: null
          }
        },
        barRoundness: 1,
        scales: {
          x: {
            stacked: true,
          },
          y: {
            stacked: true
          }
        }
      }
    };

    model.res_get_dashboard.tipologia_danno_per_citta.forEach((x: KeyValoreStrNumArr) => {
      //@ts-ignore
      configTipologiaDannoCitta.data.labels.push(x.descr_provincia);

      let tipologiaDanno: Array<string[]> = (x.tipologia_danno as unknown as Array<string[]>);

      for (let odgIndx: number = 0; odgIndx < tipologiaDanno.length; odgIndx++) {
        let labels: string[] = tipologiaDanno[odgIndx];
        for (let i = 0; i < labels.length; i++) {
          let label: string = labels[i];
          let value: number = (x.percentuale as number[])[odgIndx];

          //@ts-ignore
          let indx: number = Model.lista_tipologia_danno_per_citta.indexOf(label);

          //@ts-ignore
          configTipologiaDannoCitta.data.datasets[indx].data.push(value);
        }
      };
    });

    let canvasTipologiaDannoCitta: HTMLElement | null = document.getElementById('canvas_tipologia_danno_per_citta');

    //@ts-ignore
    chartTipologiaDannoCitta = new Chart(canvasTipologiaDannoCitta, configTipologiaDannoCitta);

    if (!chartTipologiaDannoCittaExists) {
      chartTipologiaDannoCittaExists = true;

      //@ts-ignore
      canvasTipologiaDannoCitta.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartTipologiaDannoCitta.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("id_localita", chartTipologiaDannoCitta.data.labels[res[0].index]);
      };
    }
  }
}

function updateFilters(): void {
  filtersHtml.value = "";

  if (model.res_flow_dashboard.tipologia_danno != "") {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Tipologia danno" onclick="window.clearSingleFilter('tipologia_danno')">Tipologia Danno: ${model.res_flow_dashboard.tipologia_danno}<i class="bi bi-x"></i></span>`;
  }

  if (model.res_flow_dashboard.tipologia_danno_patrimoniale != "") {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Danno patrimoniale" onclick="window.clearSingleFilter('tipologia_danno_patrimoniale')">Danno Patrimoniale: ${model.res_flow_dashboard.tipologia_danno_patrimoniale}<i class="bi bi-x"></i></span>`;
  }

  if (model.res_flow_dashboard.tipologia_danno_non_patrimoniale) {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Danno non patrimoniale" onclick="window.clearSingleFilter('tipologia_danno_non_patrimoniale')">Danno Non patrimoniale: ${model.res_flow_dashboard.tipologia_danno_non_patrimoniale}<i class="bi bi-x"></i></span>`;
  }

  if (model.res_flow_dashboard.domanda_accolta != "") {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Domanda accolta" onclick="window.clearSingleFilter('domanda_accolta')">Domanda Accolta: ${model.res_flow_dashboard.domanda_accolta}<i class="bi bi-x"></i></span>`;
  }

  if (model.res_flow_dashboard.risarcimento != "") {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Risarcimento" onclick="window.clearSingleFilter('risarcimento')">Risarcimento: ${model.res_flow_dashboard.risarcimento}<i class="bi bi-x"></i></span>`;
  }

  if (model.res_flow_dashboard.data_active) {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Anno" onclick="window.clearSingleFilter('data_active')">${model.res_flow_dashboard.data_da} - ${model.res_flow_dashboard.data_a} <i class="bi bi-x"></i></span>`;
  }

  if (model.res_flow_dashboard.id_localita != "") {
    let nomeCitta: string = model.res_flow_dashboard.id_localita;
    let findCitta: KeyValoreStr | undefined = model.res_lista_citta.find((x: KeyValoreStr): boolean => { return x.id_localita == model.res_flow_dashboard.id_localita });

    if (findCitta) nomeCitta = findCitta.citta;

    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Città" onclick="window.clearSingleFilter('id_localita')">${nomeCitta}<i class="bi bi-x"></i></span>`;
  }

  if (model.res_flow_dashboard.giudice != "") {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Giudice" onclick="window.clearSingleFilter('giudice')">${model.res_flow_dashboard.giudice}<i class="bi bi-x"></i></span>`;
  }

  if (model.res_flow_dashboard.durata_procedimento_notNull) {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Durata procedimento presente" onclick="window.clearSingleFilter('durata_procedimento_notNull')">Durata procedimento presente<i class="bi bi-x"></i></span>`;
  }
  if (model.res_flow_dashboard.durata_procedimento_active) {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Durata del procedimento" onclick="window.clearSingleFilter('durata_procedimento_active')">Durata procedimento ${model.res_flow_dashboard.durata_procedimento}<i class="bi bi-x"></i></span>`;
  }

  if (model.res_flow_dashboard.risarcimento_richiesto_active) {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Risarcimento richiesto" onclick="window.clearSingleFilter('risarcimento_richiesto_active')">` +
      `Risarcimento Richiesto: ${model.res_flow_dashboard.risarcimento_richiesto_da} - ${model.res_flow_dashboard.risarcimento_richiesto_a}` +
      `<i class="bi bi-x"></i></span>`;
  }

  if (model.res_flow_dashboard.risarcimento_disposto_totale_active) {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Risarcimento riconosciuto" onclick="window.clearSingleFilter('risarcimento_disposto_totale_active')">` +
      `Risarcimento Riconosciuto: ${model.res_flow_dashboard.risarcimento_disposto_totale_da} - ${model.res_flow_dashboard.risarcimento_disposto_totale_a}` +
      `<i class="bi bi-x"></i></span>`;
  }
}

function updateChoices(): void {
  createChoiceCitta();
  if (choiceCitta) choiceCitta.setChoiceByValue(model.res_flow_dashboard.id_localita);

  createChoiceGiudice();
  if (choiceGiudice) choiceGiudice.setChoiceByValue(model.res_flow_dashboard.giudice);
}

window.addEventListener(
    "listAllRefresh",
    (event) => {
      updateComponents();
    }
);

// COMPONENTS CLICK ----------------------
function clickOnChart(name: string, value: any) {
  let doIt: boolean = true;

  if (value.toString().indexOf(" (") >= 0) {
    let valuespl = value.split(" (");
    value = valuespl[0];
  }

  if (name == "id_localita") {
    if (value == "Other" || value == "other" || value == "Altre" || value == "Altro" || value == "Altri" || value == "altre" || value == "altro" || value == "altri" ) {
      doIt = false;
    } else {
      let findCitta: KeyValoreStr | undefined = model.res_lista_citta.find((x: KeyValoreStr): boolean => {return x.citta == value });
      if (findCitta) value = findCitta.id_localita;
    }
  }

  if (doIt) {
    //@ts-ignore
    model.res_flow_dashboard[name] = value;

    changeDashboardAt(name);
  }
}
function clickOnBox(name: string, value: any) {
  let doIt: boolean = true;
  switch(name) {
    case "durata_procedimento":
      model.res_flow_dashboard.durata_procedimento_notNull = true;
      break;
    case "risarcimento_richiesto":
      model.res_flow_dashboard.risarcimento_richiesto_da = model.res_flow_dashboard.risarcimento_richiesto_min;
      model.res_flow_dashboard.risarcimento_richiesto_a = model.res_flow_dashboard.risarcimento_richiesto_max;
      model.res_flow_dashboard.risarcimento_richiesto_active = true;
      break;
    case "risarcimento_disposto_totale":
      model.res_flow_dashboard.risarcimento_disposto_totale_da = model.res_flow_dashboard.risarcimento_disposto_totale_min;
      model.res_flow_dashboard.risarcimento_disposto_totale_a = model.res_flow_dashboard.risarcimento_disposto_totale_max;
      model.res_flow_dashboard.risarcimento_disposto_totale_active = true;
      break;
  }
  if (doIt) changeDashboardAt(name);
}

// SIDEBAR ----------------------
function toggleSidebar(): void {
  model.sidebarCollapsed = !model.sidebarCollapsed;
  let colSX: HTMLElement | null = document.getElementById("colonnaSX");
  let colDX: HTMLElement | null = document.getElementById("colonnaDX");
  if (model.sidebarCollapsed) {
    if (colSX) colSX.style.display = "none";
    if (colDX) {
      colDX.classList.remove("col-md-9", "col-lg-10");
      colDX.classList.add("col-12");
    }
  } else {
    if (colSX) colSX.style.display = "block";
    if (colDX) {
      colDX.classList.remove("col-12");
      colDX.classList.add("col-md-9", "col-lg-10");
    }
  }
}

// SLIDERS IN PAGE ----------------------
function disableActive(wtf: string): void {
  if (wtf == 'durata_procedimento') {
    model.res_flow_dashboard.durata_procedimento_active = false;
  }
}

function manageActive(wtf: string): void {
  if (wtf == 'durata_procedimento') {
    model.res_flow_dashboard.durata_procedimento_active = !model.res_flow_dashboard.durata_procedimento_active;
  }
  
  changeDashboardAt(wtf);
}

// SLIDERS MINMAX + UPDATE ----------------------
const fromMinMaxSlider = (active: boolean, cid: string) => {
  //@ts-ignore
  model.res_flow_dashboard[cid + "_active"] = active;

  changeDashboardAt(cid)
};

const minMaxSlider_risarcimento_richiesto = ref(null);
const minMaxSlider_risarcimento_disposto_totale = ref(null);
const minMaxSlider_data = ref(null);

const updateMinMaxSliders = (doSet: boolean = false): void => {
  try {
    if (doSet) {
      //@ts-ignore
      minMaxSlider_risarcimento_richiesto.value.updateMinMaxSlider(model.res_flow_dashboard.risarcimento_richiesto_da, model.res_flow_dashboard.risarcimento_richiesto_a)
      //@ts-ignore
      minMaxSlider_risarcimento_disposto_totale.value.updateMinMaxSlider(model.res_flow_dashboard.risarcimento_disposto_totale_da, model.res_flow_dashboard.risarcimento_disposto_totale_a);
      //@ts-ignore
      minMaxSlider_data.value.updateMinMaxSlider(model.res_flow_dashboard.data_da, model.res_flow_dashboard.data_a);
    } else {
      //@ts-ignore
      minMaxSlider_risarcimento_richiesto.value.updateMinMaxSlider()
      //@ts-ignore
      minMaxSlider_risarcimento_disposto_totale.value.updateMinMaxSlider();
      //@ts-ignore
      minMaxSlider_data.value.updateMinMaxSlider();
    }
  } catch (e) {
    //console.log("%%%%", e)
  }
}

// MAP + UPDATE ----------------------
let markers: Array<any> = [];
let map: any = undefined;

//@ts-ignore
let markersClickFunction = ({domEvent, latLng}) => {
  const {target} = domEvent;
  
  model.res_flow_dashboard.id_localita = target.getAttribute("id_localita");

  changeDashboardAt("id_localita");
}
async function initMap() {
  //@ts-ignore
  const {Map} = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
  map = new Map(document.getElementById('map') as HTMLElement, {
    center: {lat: 42.06842343842517, lng: 13.251924413256154},
    zoom: 6,
    mapId: 'strategiaMap666'
  });
  updateMap();
}
initMap();
async function updateMap() {
  //@ts-ignore
  const {AdvancedMarkerElement} = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
  //google.maps.event.removeListener(clickFunction);
  markers.forEach((marker: any) => {
    //marker.removeListener(markersClickFunction);
    marker.setMap(null);
    marker.dispose(); //= null;
  })
  markers = [];

  model.res_get_dashboard.totale_sentenze_per_tribunale.forEach((x: KeyValoreStrNum) => {
    const markerTag = document.createElement("div");
    markerTag.title = x.tribunale.toString();
    markerTag.className = "marker-tag";

    //eliminato il totale tra parentesi
    //markerTag.textContent = x.tribunale.toString() + " (" + x.tot.toString() + ")";
    markerTag.textContent = x.tribunale.toString();

    markerTag.setAttribute("id_localita", x.id_localita.toString());

    const marker = new AdvancedMarkerElement({
      map,
      position: {lat: Number(x.lat), lng: Number(x.long)},
      content: markerTag,
      title: x.tribunale.toString(),
      gmpClickable: true
    });
    marker.addListener("click", markersClickFunction);
    markers.push(marker);
  })
}

//DOCUMENTI
const paginazione_component = ref(null);
const paginazione_jump_component = ref(null);

function sintetizzaConAi(estremi: string, id_doc_master: number, docRigaIndex: number): void {
  gtagO({
    event: 'sintetizza AI',
    target: estremi
  })

  CommsResponsabilita.getInstance().openSintesi('responsabilita-dashboard', docRigaIndex, {
    id_doc_master: id_doc_master,
    username: model.user.profile.userId,
    crmid: model.user.profile.crmId
  });
}

function apriDettaglio(estremi: string = "", id_doc_master: number = -1, id_databanks: number = -1, id: string =""): void {
  gtagO({
    event: 'apri dettaglio',
    target: estremi,
    destinazione: model.abbonatoDejure ? 'dejure' : 'overlay'
  })

  let comms: CommsResponsabilita = CommsResponsabilita.getInstance();
  if (id_doc_master < 0) {
    if (!model.openingDejure) {
      let allIds: number[] = [];
      model.res_get_dashboard.sentenze_paginate.forEach((item: KeyValoreStrNum) => {
        if (item.estremi) {
          allIds.push(item.id_doc_master as number)
        }
      })
      if (model.abbonatoDejure) {
        model.openingDejure = true;
        comms.callDejure(allIds, ()=> { model.openingDejure = false});
      }
    }
  } else {
    if (model.abbonatoDejure) {
      comms.openDettaglioDejure(id_doc_master, id_databanks, id);
    } else {
      comms.openDettaglioModal(id_doc_master, id_databanks, id);
    }
  }
}
const updatePaginazione = (): void => {
  try {
    //@ts-ignore
    paginazione_component.value.updatePaginazione();
    //@ts-ignore
    if (paginazione_jump_component && paginazione_jump_component.value) paginazione_jump_component.value.updatePaginazione();
  } catch (e) {
    //console.log("%%%%", e)
  }
}

//REPORT
function generaReport(): void {
  let sFiltriAttivi = "";

  if (model.res_flow_dashboard.tipologia_danno != "") {
    sFiltriAttivi = "Tipologia danno";
  }
  if (model.res_flow_dashboard.tipologia_danno_patrimoniale) {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Danno patrimoniale";
  }
  if (model.res_flow_dashboard.tipologia_danno_non_patrimoniale != "") {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Danno non patrimoniale";
  }
  if (model.res_flow_dashboard.domanda_accolta != "") {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Domanda accolta";
  }
  if (model.res_flow_dashboard.risarcimento != "") {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Risarcimento";
  }

  if (model.res_flow_dashboard.durata_procedimento > 0) {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Durata Procedimento";
  }

  if (model.res_flow_dashboard.id_localita != "") {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Citta";
  }

  if (model.res_flow_dashboard.giudice != "") {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Giudice";
  }

  if (model.res_flow_dashboard.risarcimento_richiesto_active) {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Risarcimento richiesto";
  }

  if (model.res_flow_dashboard.risarcimento_disposto_totale_active) {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Risarcimento disposto totale";
  }

  PdfGenerator.res_generatePdfDashboard("responsabilita");

  gtagO({
    event: 'genera report',
    filtriAttivi: sFiltriAttivi
  })

}
</script>

<template>
  <div class="container-fluid dashboard-page">
    <div class="row">
      <!-- COLONNA SX: FILTRI -->
      <div id="colonnaSX" class="sidebar border border-right border-top-0 col-md-3 col-lg-2 p-0">
        <div tabindex="-1" id="sidebarMenu" aria-labelledby="sidebarMenuLabel">
          <div id="sidebarMenuInner" class="d-md-flex flex-column p-0 pt-lg-3 overflow-y-auto overflow-x-hidden">
            <!-- FILTRI ATTIVI -->
            <h6 class="sidebar-heading d-flex justify-content-between align-items-center px-3 mt-1 mb-1 text-body-secondary text-uppercase">
              <span>Filtri attivi</span>
              <a class="link-secondary" href="#" aria-label="" @click="clearAllFilters()" title="Pulisci tutti i filtri">
                <i class="bi bi-x-circle"></i>
              </a>
            </h6>
            <div class="px-2 filters-badges" v-html="filtersHtml"></div>

            <!-- FILTRI -->
            <hr class="my-3">
            <div class="itemFiltri">
              <label for="input_tipologia_danno">Tipologia danno</label>

              <select class="form-select" id="input_tipologia_danno" @change="changeDashboardAt('tipologia_danno')" v-model="model.res_flow_dashboard.tipologia_danno">
                <option value="">Scegli...</option>
                <option v-for="(item) in Model.lista_tipologia_danno" :value="item" :key="'tipologia_danno_' + item">
                  {{item}}
                </option>
              </select>
            </div>

            <div class="itemFiltri">
              <label for="input_tipologia_danno_patrimoniale">Danno patrimoniale</label>

              <select class="form-select" id="input_tipologia_danno_patrimoniale" @change="changeDashboardAt('tipologia_danno_patrimoniale')" v-model="model.res_flow_dashboard.tipologia_danno_patrimoniale">
                <option value="">Scegli...</option>
                <option v-for="(item) in Model.lista_tipologia_danno_patrimoniale" :value="item" :key="'tipologia_danno_patrimoniale_' + item">
                  {{item}}
                </option>
              </select>
            </div>

            <div class="itemFiltri">
              <label for="input_tipologia_danno_non_patrimoniale">Danno non patrimoniale</label>

              <select class="form-select" id="input_tipologia_danno_non_patrimoniale" @change="changeDashboardAt('tipologia_danno_non_patrimoniale')" v-model="model.res_flow_dashboard.tipologia_danno_non_patrimoniale">
                <option value="">Scegli...</option>
                <option v-for="(item) in Model.lista_tipologia_danno_non_patrimoniale" :value="item" :key="'tipologia_danno_non_patrimoniale_' + item">
                  {{item}}
                </option>
              </select>
            </div>

            <div class="itemFiltri">
              <label for="input_domanda_accolta">Domanda accolta</label>

              <select class="form-select" id="input_domanda_accolta" @change="changeDashboardAt('domanda_accolta')" v-model="model.res_flow_dashboard.domanda_accolta">
                <option value="">Scegli...</option>
                <option v-for="(item) in Model.lista_domanda_accolta" :value="item" :key="'domanda_accolta_' + item">
                  {{item}}
                </option>
              </select>
            </div>

            <div class="itemFiltri">
              <label for="input_risarcimento">Risarcimento</label>

              <select class="form-select" id="input_risarcimento" @change="changeDashboardAt('risarcimento')" v-model="model.res_flow_dashboard.risarcimento">
                <option value="">Scegli...</option>
                <option v-for="(item) in Model.lista_risarcimento" :value="item" :key="'risarcimento_' + item">
                  {{item}}
                </option>
              </select>
            </div>

            <div class="itemFiltri">
              <label for="">Risarcimento richiesto</label>
              <div class="slider-cont">
                <MinMaxSlider ref="minMaxSlider_risarcimento_richiesto"
                  v-model:min="model.res_flow_dashboard.risarcimento_richiesto_min" v-model:max="model.res_flow_dashboard.risarcimento_richiesto_max"
                  :step="Model.lista_valori_assegno_minMax[2]"
                  v-model:active="model.res_flow_dashboard.risarcimento_richiesto_active"
                  v-model:min-value="model.res_flow_dashboard.risarcimento_richiesto_da" v-model:max-value="model.res_flow_dashboard.risarcimento_richiesto_a"
                  :cid="'risarcimento_richiesto'" :changeFunct="fromMinMaxSlider" :section="'responsabilita'"
                />

                <div class="legenda">
                  <span v-html="model.res_flow_dashboard.risarcimento_richiesto_active? '[A] ':'[N] '"></span>
                  <span v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.res_flow_dashboard.risarcimento_richiesto_da)"></span>
                  &nbsp; <i class="bi bi-arrows"></i> &nbsp;
                  <span v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.res_flow_dashboard.risarcimento_richiesto_a)"></span>
                </div>
              </div>
            </div>

            <div class="itemFiltri">
              <label for="">Risarcimento riconosciuto</label>
              <div class="slider-cont">
                <MinMaxSlider ref="minMaxSlider_risarcimento_disposto_totale"
                  v-model:min="model.res_flow_dashboard.risarcimento_disposto_totale_min" v-model:max="model.res_flow_dashboard.risarcimento_disposto_totale_max"
                  :step="Model.lista_valori_assegno_minMax[2]"
                  v-model:active="model.res_flow_dashboard.risarcimento_disposto_totale_active"
                  v-model:min-value="model.res_flow_dashboard.risarcimento_disposto_totale_da" v-model:max-value="model.res_flow_dashboard.risarcimento_disposto_totale_a"
                  :cid="'risarcimento_disposto_totale'" :changeFunct="fromMinMaxSlider" :section="'responsabilita'"
                />

                <div class="legenda">
                  <span v-html="model.res_flow_dashboard.risarcimento_disposto_totale_active? '[A] ':'[N] '"></span>
                  <span v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.res_flow_dashboard.risarcimento_disposto_totale_da)"></span>
                  &nbsp; <i class="bi bi-arrows"></i> &nbsp;
                  <span v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.res_flow_dashboard.risarcimento_disposto_totale_a)"></span>
                </div>
              </div>
            </div>

            <div class="itemFiltri">
              <label for="">Anno</label>
              <div class="slider-cont">
                <MinMaxSlider ref="minMaxSlider_data"
                  v-model:active="model.res_flow_dashboard.data_active"
                  v-model:min="model.res_flow_dashboard.data_min" v-model:max="model.res_flow_dashboard.data_max"
                  :step="1"
                  v-model:min-value="model.res_flow_dashboard.data_da" v-model:max-value="model.res_flow_dashboard.data_a"
                  :cid="'data'" :changeFunct="fromMinMaxSlider" :section="'responsabilita'"
                />

                <div class="legenda">
                  <span v-html="model.res_flow_dashboard.data_active? '[A] ':'[N] '"></span>
                  <span v-html="model.res_flow_dashboard.data_da"></span>
                  &nbsp; <i class="bi bi-arrows"></i> &nbsp;
                  <span v-html="model.res_flow_dashboard.data_a"></span>
                </div>
              </div>
            </div>

            <div class="itemFiltri">
              <label for="input_id_localita">Città</label>
              <select class="form-select js-choice" id="input_id_localita" v-model="model.res_flow_dashboard.id_localita" @change="changeDashboardAt('id_localita')">
              </select>
            </div>

            <div class="itemFiltri">
              <label for="input_giudice">Giudice</label>
              <select class="form-select js-choice" id="input_giudice" v-model="model.res_flow_dashboard.giudice" @change="changeDashboardAt('giudice')">
              </select>
            </div>

            <!-- ALTRIFILTRI -->
            <hr class="my-3">
            <h6 class="sidebar-heading d-flex justify-content-between align-items-center px-3 mt-0 mb-1 text-body-secondary text-uppercase cursor-pointer" data-bs-target="#collapseAltriFiltri" data-bs-toggle="collapse">
              <span>Altri filtri</span>
              <span>
                <i class="bi bi-arrows-collapse"></i>
              </span>
            </h6>

            <div class="collapse show" id="collapseAltriFiltri">
              <hr class="my-3">

              <div class="itemFiltri">
                <label for="">Durata del procedimento</label>
                <div class="slider-cont">
                  <input @input="disableActive('durata_procedimento')" type="range" class="form-range range_input" min="0" max="10000" step="1"
                    id="input_durata_procedimento" v-model="model.res_flow_dashboard.durata_procedimento"/>
                  <span class="range_value">
                    <input @input="disableActive('durata_procedimento')" type="number" class="form-range" min="0" max="10000" step="1"
                      id="input_durata_procedimento2" v-model="model.res_flow_dashboard.durata_procedimento"/>
                  </span>

                  <div class="form-check form-switch">
                    <button @click="manageActive('durata_procedimento')" class="btn btn-sm btn-primary btn-for-slider" v-if="!model.res_flow_dashboard.durata_procedimento_active">
                      Applica
                    </button>
                    <button @click="manageActive('durata_procedimento')" class="btn btn-sm btn-danger btn-for-slider" v-else>
                      Rimuovi
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <hr class="my-3">
          </div>
        </div>
      </div>

        <!-- COLONNA DX: GRAFICI E BOX -->
      <main id="colonnaDX" class="col-md-9 ms-sm-auto col-lg-10 px-md-4 mb-4 pt-1 colonnaDxResponsabilita">
        <button id="toggleColonnaSX" class="btn" @click="toggleSidebar()" title="Mostra/Nascondi Filtri">
          <i class="bi bi-list"></i>
        </button>
      
        <!-- GRAFICI -->
        <div class="row">
          <!-- Inizio riga zero box : numero e durata -->
          <div class="col-6 p-2">
            <div class="itemBox">
              <h4>Numero sentenze prese in esame</h4>
              <h5 v-html="model.res_get_dashboard.totale_numero_sentenze.valore"></h5>
              <h6 v-if="model.res_get_dashboard.totale_numero_sentenze?.valore > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>
            </div>
          </div>
          <div class="col-6 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_durata_procedimento"
                data-bs-title="Questo dato ti permette di filtrare i processi in base alla loro durata media.
                <br>Clicca qui se vuoi filtrare le sentenze ottenendo solo quelle per cui è possibile risalire alla durata complessiva del procedimento.">
                <i class="bi bi-info-circle-fill"></i>
              </button>
              <h4 class="clickableBox" @click="clickOnBox('durata_procedimento', model.res_get_dashboard.durata_del_processo.valore)">Durata processo</h4>
              <h5 v-html="model.res_get_dashboard.durata_del_processo.valore" class="clickableBox" @click="clickOnBox('durata_procedimento', model.res_get_dashboard.durata_del_processo.valore)"></h5>
              <h6 v-if="model.res_get_dashboard.durata_del_processo?.valore > 0">Il 50% dei processi ha una durata in mesi superiore</h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>
            </div>
          </div>
          <!-- Fine riga zero box -->

          <!-- Inizio prima riga grafici : tipoligia danno + patrimoniale + non patrimoniale -->
          <div class="col-4 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_tipologia_danno"
                data-bs-title="Questo grafico ti permette di scegliere tra le sentenze relative ai procedimenti di risarcimento del danno patrimoniale, non patrimoniale o entrambe le ipotesi
Scegli il filtro di tuo interesse.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Tipologia Danno</h4>
              <h6 v-if="model.res_get_dashboard.tipologia_danno?.length > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <canvas id="canvas_tipologia_danno"></canvas>

              <div id="legend-container_tipologia_danno"></div>
            </div>
          </div>
          <div class="col-4 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_tipologia_danno_patrimoniale"
                data-bs-title="Questo grafico ti permette di scegliere tra le diverse tipologie di danno patrimoniale. Scegli il filtro di tuo interesse.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Danno patrimoniale</h4>
              <h6 v-if="model.res_get_dashboard.tipologia_danno_patrimoniale?.length > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <canvas id="canvas_tipologia_danno_patrimoniale"></canvas>

              <div id="legend-container_tipologia_danno_patrimoniale"></div>
            </div>
          </div>
          <div class="col-4 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_tipologia_danno_non_patrimoniale"
                data-bs-title="Questo grafico ti permette di scegliere tra le diverse tipologie di danno non patrimoniale. Scegli il filtro di tuo interesse.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Danno non patrimoniale</h4>
              <h6 v-if="model.res_get_dashboard.tipologia_danno_non_patrimoniale?.length > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <canvas id="canvas_tipologia_danno_non_patrimoniale"></canvas>

              <div id="legend-container_tipologia_danno_non_patrimoniale"></div>
            </div>
          </div>
          <!-- Fine prima riga grafici -->

          <!-- Inizio seconda riga grafici: domanda accolta, risarcimento -->
          <div class="col-6 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_domanda_accolta"
                data-bs-title="Questo grafico ti permette di scegliere le sentenze in cui vi in cui vi è stato accoglimento della domanda di risarcimento oppure no. Scegli il filtro di tuo interesse.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Domanda accolta</h4>
              <h6 v-if="model.res_get_dashboard.domanda_accolta?.length > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <canvas id="canvas_domanda_accolta"></canvas>

              <div id="legend-container_domanda_accolta"></div>
            </div>
          </div>
          <div class="col-6 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_risarcimento"
                data-bs-title="Questo grafico ti permette di selezionare la tipologia di risarcimento.
                Scegli il valore di tuo interesse.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Risarcimento</h4>
              <h6 v-if="model.res_get_dashboard.risarcimento?.length > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <canvas id="canvas_risarcimento"></canvas>

              <div id="legend-container_risarcimento"></div>
            </div>
          </div>
          <!-- Fine seconda riga grafici-->

          <!-- Inizio terza riga box: risarcimento richiesto , riconosciuto -->
          <div class="col-6 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_risarcimento_richiesto"
                data-bs-title="Individua il valore o un intervallo di valori dell’importo richiesto a titolo di risarcimento">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4 class="clickableBox" @click="clickOnBox('risarcimento_richiesto', model.res_get_dashboard.risarcimento_richiesto.valore)">
                Risarcimento richiesto
              </h4>
              <h5 v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.res_get_dashboard.risarcimento_richiesto.valore)"
                class="clickableBox" @click="clickOnBox('risarcimento_richiesto', model.res_get_dashboard.risarcimento_richiesto.valore)"></h5>
              <h6 v-if="model.res_get_dashboard.risarcimento_richiesto?.valore > 0">Il 50% degli importi ha un valore superiore a questa cifra</h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>
            </div>
          </div>
          <div class="col-6 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_risarcimento_disposto_totale"
                data-bs-title="Individua il valore o un intervallo di valori dell’importo riconosciuto a titolo di risarcimento.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4 class="clickableBox" @click="clickOnBox('risarcimento_disposto_totale', model.res_get_dashboard.risarcimento_disposto_totale.valore)">
                Risarcimento riconosciuto
              </h4>
              <h5 v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.res_get_dashboard.risarcimento_disposto_totale.valore)"
                class="clickableBox" @click="clickOnBox('risarcimento_disposto_totale', model.res_get_dashboard.risarcimento_disposto_totale.valore)"></h5>
              <h6 v-if="model.res_get_dashboard.risarcimento_disposto_totale?.valore > 0">Il 50% degli importi ha un valore superiore a questa cifra</h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>
            </div>
          </div>
          <!-- Fine terza riga -->

          <!-- Inizio quarta riga bicolonnare: sx durata + tipologia per città e dx mappa -->
          <div class="col-6 p-2">
            <div class="itemBox itemBoxDouble">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_durata_del_processo_per_singola_citta"
                data-bs-title="Questo grafico ti permette di verificare la durata di un processo in base al luogo in cui si è svolto.<br> Clicca sulla singola città per 
                avere l’indicazione diretta dell’andamento in quel foro.<br>Scegli il filtro di tuo interesse.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Durata del processo per singola città</h4>
              <h6 v-if="model.res_get_dashboard.durata_del_processo_per_singola_citta?.length > 0">
                Il 50% dei processi ha avuto una durata in mesi superiore al valore indicato
              </h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>
              <canvas id="canvas_durata_del_processo_per_singola_citta"></canvas>
            </div>

            <div class="itemBox itemBoxDouble">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_tipologia_danno_per_citta"
                data-bs-title="Il grafico riporta la distribuzione per città degli esiti dei processi oggetto di analisi.<br>Clicca sulla tipologia di esito del processo 
                per avere l’indicazione specifica rispetto al singolo foro.<br>Scegli il filtro di tuo interesse.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Tipologia danno per città</h4>
              <h6 v-if="model.res_get_dashboard.tipologia_danno_per_citta?.length > 0">
                Distribuzione tipologia danno
              </h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>
              <canvas id="canvas_tipologia_danno_per_citta"></canvas>
            </div>
          </div>
          <!-- MAPPA -->
          <div class="col-6 p-2">
            <div class="itemBox itemBoxMap">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true"
                      data-bs-title="Clicca sulla singola città per applicare il relativo filtro">
                <i class="bi bi-info-circle-fill"></i>
              </button>
              <h4>Mappa</h4>
              <h6 v-if="model.res_get_dashboard.totale_sentenze_per_tribunale?.length > 0"><!-- Lorem ipsum --></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <div id="map"></div>
            </div>
          </div>
          <!-- Fine quarta riga bicolonnare -->

          <!-- SENTENZE -->
          <div class="col-12 p-2">
            <div class="itemBox itemBoxLast">
              <h4>Sentenze</h4>

              <div class="table-responsive small table-documenti" v-if="model.res_get_dashboard.sentenze_paginate && model.res_get_dashboard.sentenze_paginate.length && model.res_get_dashboard.sentenze_paginate.length > 0">
                <table class="table table-striped table-sm">
                  <tbody>
                  <template v-for="(item, index) in model.res_get_dashboard.sentenze_paginate">

                    <tr v-if="item.estremi" :id="'docRiga-responsabilita-dashboard-'+index">
                      <!-- td><em>{{ (new Date(item.date)).toLocaleDateString() }}</em></td -->
                      <td>{{item.estremi}}</td>
                      <td class="text-right">
                        <a href="javascript:void(0)" @click="sintetizzaConAi(item.estremi as string, item.id_doc_master as number, index)" v-if="item.tipologia_doc != 136">
                          <i class="bi bi-stars"></i> Sintetizza con AI
                        </a>
                        <span v-else> </span>
                      </td>
                      <td class="text-right">
                        <a href="javascript:void(0)" @click="apriDettaglio(item.estremi as string, item.id_doc_master as number, item.id_databanks as number, item.id as string)">
                          <i class="bi bi-link"></i> Apri dettaglio
                        </a>
                      </td>
                    </tr>
                  </template>
                  </tbody>
                </table>
              </div>

              <div class="row" v-if="model.res_get_dashboard.sentenze_paginate && model.res_get_dashboard.sentenze_paginate.length && model.res_get_dashboard.sentenze_paginate.length > 0">
                <div class="col-12 col-sm-6 col-xl-4 paginazione-azione">
                  <button class="btn btn-sm btn-primary" @click="apriDettaglio()" v-if="model.abbonatoDejure">
                    <i class="bi bi-box-arrow-up-right" v-if="!model.openingDejure"></i> 
                    <div v-else class="spinner-border" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>&nbsp;  
                    Approfondisci su DeJure
                  </button>

                  <button class="btn btn-sm btn-error" @click="generaReport()" v-if="model.res_flow_dashboard.tipologia_danno != ''">
                    <i class="bi bi-download" v-if="!model.generatingPdf"></i>
                    <div v-else class="spinner-border" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    &nbsp; Genera report
                  </button>

                </div>
                <div class="col-12 col-sm-6 col-xl-3 paginazione-label">
                  <b>{{ model.res_flow_dashboard.paginazione_tot }}</b> Elementi Totali |
                  Pagina <b>{{ (model.res_flow_dashboard.paginazione_start + 1) }}</b> di
                  <b>{{ (model.res_flow_dashboard.paginazione_pages + 1) }}</b>
                </div>
                <div class="col-12 col-sm-12 col-xl-5">
                  <PaginazioneDashboard ref="paginazione_component" @recalc="callQueryPaginazione()"></PaginazioneDashboard>
                </div>

                <!-- principi di diritto -->
                <div class="col-12 mt-3" v-if="model.abbonatoDejure && model.res_get_dashboard.sentenze_paginate_jump_display && model.res_get_dashboard.sentenze_paginate_jump_display.length && model.res_get_dashboard.sentenze_paginate_jump_display.length > 0">
                  <h4>Principi di diritto </h4>

                  <div class="table-responsive small table-documenti">
                    <table class="table table-striped table-sm">
                      <tbody>
                      <template v-for="(item, index) in model.res_get_dashboard.sentenze_paginate_jump_display">
                        <tr v-if="item.estremi || item.id_doc_master" :id="'docRiga-responsabilita-dashboard-jump-'+index">
                          <td v-if="item.estremi">{{item.estremi}}</td>
                          <td v-else> {{item.id_doc_master}}</td>
                          <td class="text-right">
                            <a href="javascript:void(0)" @click="apriDettaglio(item.estremi as string, item.id_doc_master as number, item.id_databanks as number, item.id as string)">
                              <i class="bi bi-link"></i> Apri dettaglio
                            </a>
                          </td>
                        </tr>
                      </template>
                      </tbody>
                    </table>
                  </div>

                  <div class="row">
                    <div class="col-12 col-sm-6 col-xl-4 paginazione-azione">

                    </div>
                    <div class="col-12 col-sm-6 col-xl-3 paginazione-label">
                      <b>{{ model.res_flow_dashboard.diritto_paginazione_tot }}</b> Elementi Totali |
                      Pagina <b>{{ (model.res_flow_dashboard.diritto_paginazione_start + 1) }}</b> di
                      <b>{{ (model.res_flow_dashboard.diritto_paginazione_pages + 1) }}</b>
                    </div>
                    <div class="col-12 col-sm-12 col-xl-5">
                      <PaginazioneDashboardJump ref="paginazione_jump_component"></PaginazioneDashboardJump>
                    </div>
                  </div>

                </div>

              </div>
              <div class="row" v-else>
                <div class="col-12">
                  Non sono presenti sentenze
                </div>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  </div>
</template>